import { isNil } from 'lodash/fp';
import { SnowflakeConnectionState, type SnowflakeTableFqn } from 'app/lib/types/server';
import {
    SNOWFLAKE_CONNECTION_STATUS_TYPE,
    WAREHOUSE_HEALTH_CHECK_RESULT_TYPE
} from 'app/store/__generated__/types';
import type { ReduxActionOutcome, ReduxNonApiAction } from 'app/store/storeTypes';
import { doGetSnowflakeDatabases } from 'app/store/snowflake/actions.js';
import { doToggleConnectionStatusModal } from 'app/store/snowflakeConfiguration/actions';
import { doValidateSnowflakeAccess } from 'app/store/access/actions';
import { selectNonDeployingUpstreamSourceFqns } from 'app/store/deployView/selectors.js';
import {
    doCleanupAndInvalidateQueries,
    doInvalidateFQNQuery
} from 'app/store/snowflakeQueries/actions.js';
import { QueryKeys } from 'app/store/snowflakeQueries/reducer.js';
import {
    type SnowflakeConnectionHealthcheck,
    type Healthcheck,
    type WarehouseHealthCheck,
    CLEANUP_AND_UPDATE_STATUS,
    UPDATE_SNOWFLAKE_CLIENT_STATUS,
    UPDATE_STATUS,
    UPDATE_WAREHOUSE_HEALTH,
    TOGGLE_WAREHOUSE_LOAD_DIALOG
} from './types';
import { selectSnowflakeConnection } from './selectors';

const { CONNECTED, DISCONNECTED } = SnowflakeConnectionState;

const doUpdateAppHealthStatus = (payload: Healthcheck) =>
    ({
        type: UPDATE_STATUS,
        payload
    }) as const satisfies ReduxNonApiAction;

const doUpdateWarehouseHealth = (payload: WarehouseHealthCheck) =>
    ({
        type: UPDATE_WAREHOUSE_HEALTH,
        payload
    }) as const satisfies ReduxNonApiAction;

export const doToggleWarehouseLoadDialog = (isOpen: boolean) =>
    ({
        type: TOGGLE_WAREHOUSE_LOAD_DIALOG,
        payload: { isOpen }
    }) as const satisfies ReduxNonApiAction;

const doUpdateSnowflakeClientStatus = (status: SnowflakeConnectionHealthcheck) =>
    ({
        type: UPDATE_SNOWFLAKE_CLIENT_STATUS,
        payload: status
    }) as const satisfies ReduxNonApiAction;

export const doCleanupAndUpdateAppHealthStatus = (
    status: Healthcheck | SnowflakeConnectionHealthcheck | WarehouseHealthCheck
) =>
    ({
        type: CLEANUP_AND_UPDATE_STATUS,
        payload: status,
        effect: ({ store }) => {
            if (status._type === SNOWFLAKE_CONNECTION_STATUS_TYPE) {
                const state = store.getState();
                const previousConnectionStatus = selectSnowflakeConnection(state);

                const previousConnectionState = previousConnectionStatus?.state;
                const currentConnectionState = status.state;

                const roleHasChanged =
                    previousConnectionStatus && 'role' in previousConnectionStatus
                        ? previousConnectionStatus.role !== status.role
                        : false;
                const warehouseHasChanged =
                    !isNil(previousConnectionStatus?.warehouse) &&
                    previousConnectionStatus?.warehouse !== status.warehouse;

                if (
                    previousConnectionState === DISCONNECTED &&
                    currentConnectionState === CONNECTED
                ) {
                    store.dispatch(doGetSnowflakeDatabases());
                    store.dispatch(doToggleConnectionStatusModal(false));
                }

                if (
                    previousConnectionState === CONNECTED &&
                    currentConnectionState === DISCONNECTED
                ) {
                    store.dispatch(doToggleConnectionStatusModal(false));
                }

                if (roleHasChanged) {
                    store.dispatch(doCleanupAndInvalidateQueries(QueryKeys.FQNS));
                    store.dispatch(doCleanupAndInvalidateQueries(QueryKeys.ENTITIES));
                    store.dispatch(doCleanupAndInvalidateQueries(QueryKeys.ROW_COUNT));
                }

                if (
                    (warehouseHasChanged || roleHasChanged) &&
                    currentConnectionState === CONNECTED
                ) {
                    store.dispatch(doGetSnowflakeDatabases());
                }

                const isCurrentWarehouseDifferent =
                    previousConnectionStatus?.warehouse !== undefined &&
                    previousConnectionStatus?.warehouse !== status.warehouse;
                if (
                    (previousConnectionState &&
                        previousConnectionState !== currentConnectionState) ||
                    (currentConnectionState === CONNECTED &&
                        (roleHasChanged || isCurrentWarehouseDifferent))
                ) {
                    const fqns = selectNonDeployingUpstreamSourceFqns(
                        state
                    ) as unknown as SnowflakeTableFqn[];
                    store.dispatch(doValidateSnowflakeAccess(fqns));
                    fqns.forEach((fqn) => store.dispatch(doInvalidateFQNQuery(fqn)));
                }

                store.dispatch(doUpdateSnowflakeClientStatus(status));
            } else if (status._type === WAREHOUSE_HEALTH_CHECK_RESULT_TYPE) {
                store.dispatch(doUpdateWarehouseHealth(status));
            } else {
                store.dispatch(doUpdateAppHealthStatus(status));
            }
        }
    }) as const satisfies ReduxNonApiAction;

export type HealthcheckActionOutcomes =
    | ReduxActionOutcome<ReturnType<typeof doUpdateAppHealthStatus>>
    | ReduxActionOutcome<ReturnType<typeof doUpdateWarehouseHealth>>
    | ReduxActionOutcome<ReturnType<typeof doUpdateSnowflakeClientStatus>>
    | ReduxActionOutcome<ReturnType<typeof doCleanupAndUpdateAppHealthStatus>>
    | ReduxActionOutcome<ReturnType<typeof doToggleWarehouseLoadDialog>>;
